// Internal event schema — types consumed by the chat UI (MessageFeed,
// ToolBlock) and the WS client.

import type { ServerMsg } from './agent-protocol.ts';

export interface TextBlock { type: 'text'; text: string }
export interface ThinkingBlock { type: 'thinking'; thinking: string; budgetTokens?: number; startedAt?: number }
// Tool inputs are schema-less at the provider boundary (varies per tool).
// `startedAt` = wall-clock ms when the spawning `action` frame arrived (stamped
// by both the live fold and the replay fold). It's the HONEST per-tool start —
// distinct from the containing assistant message's timestamp, which is the turn's
// first-content-frame time and can pre-date a tool spawned after long reasoning.
// The Active-agents dock anchors each subagent's live timer to this so a
// `sleep 25` child doesn't read 0:54 because the model thought for 16s first.
// Optional: history that predates the field falls back to the message timestamp.
export interface ToolUse { type: 'tool_use'; id: string; name: string; input: Record<string, unknown>; startedAt?: number }
export interface ToolResult {
	type: 'tool_result';
	toolUseId: string;
	content: string | ContentBlock[];
	isError?: boolean;
}
export type ContentBlock = TextBlock | ThinkingBlock | ToolUse | ToolResult;

export interface TokenUsage {
	inputTokens: number;
	outputTokens: number;
	cacheCreationInputTokens: number;
	cacheReadInputTokens: number;
	// True context-window occupancy = the agent-computed prompt size of the LAST API
	// call this turn (NOT input+cacheRead+cacheCreation, which is cumulative across the
	// turn's internal calls). Drives the composer context meter. Undefined on turns
	// that predate the agent reporting it → the meter hides.
	contextTokens?: number;
	// Provider-reported usable context window for the SAME response. When present,
	// the meter uses this real capacity instead of its legacy 200k quality fallback.
	contextWindow?: number;
}

// Map a wire `response.usage` to the feed's TokenUsage. The per-call cumulative fields
// (input/cacheRead/cacheCreation) carry through for completeness, but the context METER
// uses `contextTokens` — the agent's true window-occupancy figure — because summing the
// cumulative fields over-counts (~700k for a chat whose window held ~200k).
export function responseUsageToTokenUsage(u: {
	input?: number;
	output?: number;
	cacheRead?: number;
	cacheCreation?: number;
	contextTokens?: number;
	contextWindow?: number;
}): TokenUsage {
	return {
		inputTokens: u.input ?? 0,
		outputTokens: u.output ?? 0,
		cacheReadInputTokens: u.cacheRead ?? 0,
		cacheCreationInputTokens: u.cacheCreation ?? 0,
		...(u.contextTokens !== undefined ? { contextTokens: u.contextTokens } : {}),
		...(u.contextWindow !== undefined ? { contextWindow: u.contextWindow } : {}),
	};
}

export const CONTEXT_COMPACTED_COPY =
	'Older messages were condensed into a summary — earlier details may be simplified.';

/** Ordered transcript row emitted when the provider compacts earlier context.
 *  Kept in the feed union (rather than a detached toast/banner) so live delivery
 *  and persisted history render at the exact point where compaction happened. */
export interface ContextCompactedMessage {
	role: 'system';
	type: 'context_compacted';
	content: typeof CONTEXT_COMPACTED_COPY;
	provider: 'claude' | 'codex';
	tokensBefore?: number;
	tokensAfter?: number;
	timestamp: number;
}

type ContextCompactedServerMsg = Extract<ServerMsg, { type: 'context_compacted' }>;

/** Shared wire→feed mapper used by both the live WS fold and static history fold. */
export function contextCompactedFeedMessage(
	m: ContextCompactedServerMsg,
	at: number,
): ContextCompactedMessage {
	return {
		role: 'system',
		type: 'context_compacted',
		content: CONTEXT_COMPACTED_COPY,
		provider: m.provider,
		...(m.tokensBefore !== undefined ? { tokensBefore: m.tokensBefore } : {}),
		...(m.tokensAfter !== undefined ? { tokensAfter: m.tokensAfter } : {}),
		timestamp: at,
	};
}

export interface Message {
	id?: string;
	role: 'assistant';
	content: ContentBlock[];
	stopReason?: string | null;
	parentToolUseId?: string | null;
	usage?: TokenUsage;
	timestamp: number;
	// The engine that produced this turn, stamped by ws-client from the turn's
	// `turn_phase` frame (which lands before any content). Drives the feed header's
	// author label so a Codex turn reads "OPENAI", not the old hardcoded "CLAUDE";
	// per-message so it stays correct across reload and a mid-chat provider switch.
	// Undefined for pre-turn_phase history — the header falls back to the live engine.
	provider?: 'claude' | 'codex';
}

type TurnScopedFeedMessage =
	| Message
	| ContextCompactedMessage
	| { role: 'user'; clientMsgId?: string };

function findTurnUserIndex(
	messages: readonly TurnScopedFeedMessage[],
	clientMsgId?: string,
): number {
	if (clientMsgId) {
		for (let index = messages.length - 1; index >= 0; index--) {
			const message = messages[index];
			if (message.role === 'user' && message.clientMsgId === clientMsgId) return index;
		}
	}
	for (let index = messages.length - 1; index >= 0; index--) {
		if (messages[index].role === 'user') return index;
	}
	return -1;
}

/** Insert the durable compaction marker at its provider-truthful boundary.
 * Codex reports compaction through a delayed rollout poll, but compaction itself
 * happened before generation: move the marker before this turn's first assistant
 * row, using the turn anchor even if a queued user_echo already closed it. Claude
 * reports the boundary synchronously during generation: settle the current row so
 * later content opens after the divider. */
export function appendContextCompactionBoundary(
	messages: TurnScopedFeedMessage[],
	openAssistant: Message | null,
	marker: ContextCompactedMessage,
	turnAssistantAnchor: Message | null = openAssistant,
): Message | null {
	if (marker.provider === 'codex' && turnAssistantAnchor) {
		const anchorIndex = messages.indexOf(turnAssistantAnchor);
		if (anchorIndex >= 0) {
			let insertAt = anchorIndex;
			for (let index = anchorIndex - 1; index >= 0; index--) {
				const message = messages[index];
				if (message.role === 'user') break;
				if (message.role === 'assistant') insertAt = index;
			}
			messages.splice(insertAt, 0, marker);
		} else {
			messages.push(marker);
		}
		return openAssistant;
	}
	if (openAssistant && openAssistant.stopReason == null) {
		openAssistant.stopReason = 'context_compacted';
	}
	messages.push(marker);
	return null;
}

/** A recovery rerun replaces every partial assistant row for the failed turn,
 * including rows a Claude compaction boundary already closed. Keep system
 * dividers: the compaction really happened even though its partial prose is
 * discarded. Returns the removed rows for pointer cleanup/assertions. */
export function discardPartialAssistantRows(
	messages: TurnScopedFeedMessage[],
	clientMsgId?: string,
): Message[] {
	const userIndex = findTurnUserIndex(messages, clientMsgId);
	if (userIndex < 0) return [];
	const removed: Message[] = [];
	for (let index = messages.length - 1; index > userIndex; index--) {
		const message = messages[index];
		if (message.role !== 'assistant') continue;
		removed.unshift(message);
		messages.splice(index, 1);
	}
	return removed;
}

// `code`/`provider`/`clientMsgId` ride along for a recoverable failure: the chat
// route reads the latest bridge_error and, when code is 'usage_limit' (exhausted),
// 'overloaded' (Anthropic 529), or 'auth_error' (OAuth token refresh failed) and the
// session is errored, offers a mid-chat switch to the other provider (replaying the
// failed message identified by clientMsgId). Optional — a generic kill omits them.
export interface BridgeErrorEvent {
	subtype: 'bridge_error';
	message: string;
	code?: 'usage_limit' | 'overloaded' | 'auth_error';
	provider?: 'claude' | 'codex';
	clientMsgId?: string;
}
// Backend appends these to messageHistory when a turn is killed mid-stream.
// `interrupted_by_restart` fires on boot when an orphaned reply is detected;
// `interrupted_by_shutdown` fires when SIGTERM hits an active stream during
// graceful shutdown.
export interface InterruptedByRestartEvent {
	subtype: 'interrupted_by_restart';
	originalUserMessageId: string;
	interruptedAt: number;
	uuid: string;
	sessionId: string;
}
export interface InterruptedByShutdownEvent {
	subtype: 'interrupted_by_shutdown';
	originalUserMessageId?: string;
	interruptedAt: number;
	uuid: string;
	sessionId: string;
}

export type SystemEvent =
	| BridgeErrorEvent
	| InterruptedByRestartEvent
	| InterruptedByShutdownEvent;

/** Upsert a tool_use block into a turn's content. A RE-EMITTED `action` frame
 *  with the same id (Codex web_search opens its card with an empty input at
 *  item.started and re-emits the real query at item.completed) must UPDATE the
 *  existing block instead of duplicating the card. Shared by the live fold
 *  (ws-client.svelte.ts) and the read-only replay (history-fold.ts) so the two
 *  can't drift. Mutates the found block via property assignment — the same
 *  pattern as the text-delta appends — so a Svelte 5 $state-proxied content
 *  array observes the update. Returns true when an existing block was updated
 *  in place, false when the block was pushed as new. */
export function upsertToolUse(content: ContentBlock[], block: ToolUse): boolean {
	for (const b of content) {
		if (b.type === 'tool_use' && b.id === block.id) {
			b.input = block.input;
			b.name = block.name; // defensive — the re-emit should never rename
			// Preserve the ORIGINAL startedAt: a re-emit (Codex web_search at
			// item.completed) carries a later timestamp, but the tool started when it
			// was first announced — keep that anchor so the elapsed clock is honest.
			return true;
		}
	}
	content.push(block);
	return false;
}

function renderedText(content: ContentBlock[]): string {
	return content
		.filter((b): b is TextBlock => b.type === 'text')
		.map((b) => b.text)
		.join('\n\n')
		.trim();
}

function lastTextBlock(content: ContentBlock[]): TextBlock | undefined {
	for (let i = content.length - 1; i >= 0; i--) {
		const b = content[i];
		if (b.type === 'text') return b;
	}
	return undefined;
}

function responseTextAlreadyRenderedValue(rendered: string, text: string): boolean {
	const finalText = text.trim();
	if (!finalText) return true;
	const alreadyRendered = rendered.trim();
	if (!alreadyRendered) return false;
	return (
		alreadyRendered === finalText ||
		alreadyRendered.startsWith(finalText) ||
		alreadyRendered.endsWith(finalText)
	);
}

/** True when a terminal response would add no visible text to this assistant
 * row. Used when a delayed compaction marker lands after the final streamed
 * block but before the terminal frame: the marker closes the row, yet the
 * terminal's duplicate fallback text and usage still belong to that row. */
export function responseTextAlreadyRendered(message: Message, text: string): boolean {
	return responseTextAlreadyRenderedValue(renderedText(message.content), text);
}

/** Terminal response text belongs to a logical turn, not one rendered row.
 * Compaction deliberately splits that turn into multiple assistant rows around
 * a system divider, so compare the terminal fallback against every assistant
 * row after the matching user message. Separator variants cover providers that
 * concatenate streamed blocks with zero, one, or two newlines. */
export function responseTextAlreadyRenderedInTurn(
	messages: readonly TurnScopedFeedMessage[],
	text: string,
	clientMsgId?: string,
): boolean {
	const userIndex = findTurnUserIndex(messages, clientMsgId);

	const renderedRows = messages
		.slice(userIndex + 1)
		.filter((message): message is Message => message.role === 'assistant')
		.map((message) => renderedText(message.content))
		.filter(Boolean);
	if (renderedRows.length === 0) return false;

	const variants = [
		renderedRows.join(''),
		renderedRows.join('\n'),
		renderedRows.join('\n\n'),
	];
	const normalizedFinal = text.trim().replace(/\s+/g, ' ');
	return variants.some(
		(rendered) =>
			responseTextAlreadyRenderedValue(rendered, text) ||
			rendered.trim().replace(/\s+/g, ' ') === normalizedFinal,
	);
}

// Terminal `response.text` usually duplicates streamed text deltas, but synthetic
// terminal messages (rate/session limits) can arrive only on the response frame.
export function appendResponseTextIfMissing(message: Message, text: string): boolean {
	const finalText = text.trim();
	if (!finalText) return false;

	const alreadyRendered = renderedText(message.content);
	const lastText = lastTextBlock(message.content);
	if (!alreadyRendered) {
		if (lastText) lastText.text = finalText;
		else message.content.push({ type: 'text', text: finalText });
		return true;
	}
	// endsWith covers multi-block turns (narration → tools → answer): the terminal
	// text equals only the FINAL streamed block, never the whole joined transcript.
	if (responseTextAlreadyRendered(message, finalText)) return false;

	if (finalText.startsWith(alreadyRendered)) {
		const suffix = finalText.slice(alreadyRendered.length).trimStart();
		if (!suffix) return false;
		if (lastText) lastText.text += suffix;
		else message.content.push({ type: 'text', text: finalText });
		return true;
	}

	message.content.push({ type: 'text', text: finalText });
	return true;
}
